import { reader } from '@woonstad/helpers-utils';

export type WatermarkOptions = {
  purpose: string;
  date?: string;
};

function formatDate(): string {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();

  return `${day}-${month}-${year}`;
}

export async function watermarkImage(
  file: File,
  options: WatermarkOptions,
): Promise<File> {
  if (!file.type.startsWith('image/')) {
    return file;
  }

  const base64 = await reader(file);
  const dateStr = options.date ?? formatDate();

  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const {
        width,
        height
      } = img;

      const canvas = document.createElement('canvas');

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');

      if (!ctx) {
        resolve(file);

        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Diagonal "KOPIE" watermark across the center
      const diagonalLength = Math.sqrt(width * width + height * height);
      const fontSize = Math.max(width * 0.15, 5);

      ctx.save();
      ctx.translate(width / 2, height / 2);
      ctx.rotate(-Math.atan2(height, width));
      ctx.font = `bold ${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const text = 'KOPIE';
      const textWidth = ctx.measureText(text).width;
      const repeatCount = Math.ceil(diagonalLength / (textWidth + fontSize)) + 1;

      for (let i = -repeatCount; i <= repeatCount; i++) {
        const x = i * (textWidth + fontSize);

        ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
        ctx.fillText(text, x + 2, 2);

        ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
        ctx.fillText(text, x, 0);
      }

      ctx.restore();

      // Bottom bar with date + purpose
      const barHeight = Math.max(height * 0.06, 32);
      const barFontSize = Math.max(barHeight * 0.45, 14);

      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(0, height - barHeight, width, barHeight);

      ctx.font = `${barFontSize}px sans-serif`;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.textBaseline = 'middle';

      const barY = height - barHeight / 2;
      const padding = barHeight * 0.4;

      ctx.textAlign = 'left';
      ctx.fillText(`${options.purpose}`, padding, barY);

      ctx.textAlign = 'right';
      ctx.fillText(dateStr, width - padding, barY);

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(new File([blob], file.name, { type: file.type || 'image/jpeg' }));
        } else {
          resolve(file);
        }
      }, file.type || 'image/jpeg', 0.92);
    };

    img.onerror = () => reject(new Error(`Failed to load image: ${file.name}`));
    img.src = base64;
  });
}
